import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import cx from 'classnames';
import { groupBy } from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';
import Steven, { ELEMENT_TYPES } from 'helpers/steven';
import { Button, Text , Notice } from '@companycam/slab-web';
import { ActionLink } from 'components/shared';
import OutsideClickObserver from 'components/shared/OutsideClickObserver';
import useMenuActionTag from 'components/assetFeed/actions/useMenuActionTag';
import useMenuActionDownload from 'components/assetFeed/actions/useMenuActionDownload';
import useMenuActionDuplicate from 'components/assetFeed/actions/useMenuActionDuplicate';
import useMenuActionPrint from 'components/assetFeed/actions/useMenuActionPrint';
import useMenuActionSetCoverPhoto from 'components/assetFeed/actions/useMenuActionSetCoverPhoto';
import useMenuActionMarkInternal from 'components/assetFeed/actions/useMenuActionMarkInternal';
import useMenuActionMove from 'components/assetFeed/actions/useMenuActionMove';
import useMenuActionDelete from 'components/assetFeed/actions/useMenuActionDelete';
import useMenuActionCreateReport from 'components/assetFeed/actions/useMenuActionCreateReport';
import useMenuActionShareGallery from 'components/assetFeed/actions/useMenuActionShareGallery';
import MenuItemBeforeAfter from './MenuItemBeforeAfter';
import UpgradeIcon from 'components/shared/DesignSystem/UpgradeIcon';
import { useModal } from 'components/shared/hooks';
import { Modal } from 'components/core/modal/constants';
import { LD_PROJECT_TRASH_CAN_FLAG } from 'constants/featureFlags';
import useCompanyBillingStatus from 'components/billing/useCompanyBillingStatus';
import { canAccessFeature, FEATURES } from 'helpers/features';
import { IntegrationActionLinks } from 'components/projects/show/integrationActions';
import { useGetPhotoIntegrationActions } from 'components/projects/show/integrationActions/useGetIntegrationActions';
import FloatingActionBar from 'components/shared/FloatingActionBar';

import { translate } from 'translations/i18n';

const AlertDiv = styled.div`
  line-height: 1.2;
  color: var(--cc_color_utility_caution_darken_15);
  background-color: var(--cc_color_utility_caution_alpha_20);
  border: 1px solid var(--cc_color_utility_caution_alpha_50);
  margin: 0 8px 8px 8px;
  padding: 8px;
  border-radius: 8px;
`;

const FloatingMenuActions = styled.div`
  display: flex;
  gap: var(--cc_size_spacing_s);
  align-items: center;
  flex-wrap: wrap;
  padding-left: var(--cc_size_spacing_s);
`;

const SelectedContainer = styled.div`
  border-right: 1px solid rgba(255, 255, 255, 0.5);
  padding-right: var(--cc_size_spacing_xl);
`;

const MenuWrapper = ({ isActive, closeMenu, children }) => (
  <OutsideClickObserver active={isActive} onClickOutside={closeMenu}>
    <div className={cx('dropdown', { 'show-contents': isActive })}>
      {children}
    </div>
  </OutsideClickObserver>
);

MenuWrapper.propTypes = {
  isActive: PropTypes.bool,
  closeMenu: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

const MenuButton = ({ icon = null, label, onClick }) => (
  <Button color="secondary" design="minimal" onClick={onClick} icon={icon}>
    {label}
  </Button>
);

MenuButton.propTypes = {
  icon: PropTypes.object,
  label: PropTypes.object,
  onClick: PropTypes.func,
};

const MenuItemsWrapper = ({ children }) => (
  <div className="dropdown-menu dropdown-menu--wide dropdown-menu--align-left light-environment">
    {children}
  </div>
);

MenuItemsWrapper.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

const MenuItemSection = ({ label }) => (
  <div className="dropdown-menu__section-title">{label}</div>
);

MenuItemSection.propTypes = {
  label: PropTypes.object,
};

const MenuItemAlert = ({ label }) => <AlertDiv>{label}</AlertDiv>;

MenuItemAlert.propTypes = {
  label: PropTypes.string,
};

const MenuItemAction = ({
  icon,
  label,
  onClick,
  showUpgradeIcon,
  disabled = false,
}) => {
  if (!onClick) {
    return null;
  }

  return (
    <div>
      <ActionLink
        className={cx('dropdown-menu__link', {
          'no-interaction': disabled,
        })}
        onClick={onClick}
        disabled={disabled}
      >
        {icon}
        {label}
        {showUpgradeIcon ? (
          <UpgradeIcon style={{ marginLeft: 'auto' }} />
        ) : null}
      </ActionLink>
    </div>
  );
};

MenuItemAction.propTypes = {
  icon: PropTypes.object,
  label: PropTypes.object,
  onClick: PropTypes.func,
  showUpgradeIcon: PropTypes.bool,
  disabled: PropTypes.bool,
};

const MENU_NONE = 'MENU_NONE';
const MENU_ACTIONS = 'MENU_ACTIONS';
const MENU_REPORTS = 'MENU_REPORTS';
const MENU_SHARE = 'MENU_SHARE';

function SelectedAssetBar(props) {
  const [activeMenu, setActiveMenu] = useState(MENU_NONE);
  const { showModal } = useModal();
  const { getAllSelected, onRequestClose, projectNodeId, projectDatabaseId } =
    props;
  const { featureSet } = useCompanyBillingStatus();

  const groupedAssets = useMemo(() => {
    const assets = getAllSelected();
    const assetsByTypename = groupBy(assets, '__typename');

    return {
      assets,
      assetsByType: {
        videos: assetsByTypename['Video'] || [],
        photos: assetsByTypename['Photo'] || [],
      },
    };
  }, [getAllSelected]);

  const selectedPhotoIds = groupedAssets.assetsByType.photos.map((obj) =>
    parseInt(obj.databaseId, 10),
  );

  const menuItemOptions = {
    ...groupedAssets,
    onSuccess: onRequestClose,
  };

  const [actionDownload, renderModalDownload] =
    useMenuActionDownload(menuItemOptions);
  const [actionTag, renderModalTag] = useMenuActionTag(menuItemOptions);
  const [actionDuplicate] = useMenuActionDuplicate(menuItemOptions);
  const [actionPrint, renderModalPrint] = useMenuActionPrint(menuItemOptions);
  const [actionSetCoverPhoto] = useMenuActionSetCoverPhoto(menuItemOptions);
  const [actionMarkInternal] = useMenuActionMarkInternal({
    ...menuItemOptions,
    internal: true,
  });
  const [actionUnmarkInternal] = useMenuActionMarkInternal({
    ...menuItemOptions,
    internal: false,
  });
  const [actionMove, renderModalMove] = useMenuActionMove(menuItemOptions);
  const [actionDelete] = useMenuActionDelete(menuItemOptions);
  const [, canAccessReportGeneration] =
    useMenuActionCreateReport(menuItemOptions);
  const flags = useFlags();
  const isTrashCanEnabled = flags[LD_PROJECT_TRASH_CAN_FLAG];
  const canAccessReportTemplates = canAccessFeature(
    featureSet,
    FEATURES.REPORT_TEMPLATES,
  );

  const [actionShareGallery, hasAccessToShareGallery, renderShareGalleryModal] =
    useMenuActionShareGallery(menuItemOptions);

  const closeMenus = () => {
    setActiveMenu(MENU_NONE);
  };

  const menuToggler = (menuEnum) => () => {
    setActiveMenu(activeMenu === menuEnum ? MENU_NONE : menuEnum);
  };

  const { photoIntegrationActions, isPhotoIntegrationActionsLoading } =
    useGetPhotoIntegrationActions();

  const isProjectPhotosPage =
    window.location.pathname.startsWith('/projects/') &&
    window.location.pathname.endsWith('/photos');
  const shouldDisplayIntegrationActions =
    isProjectPhotosPage && photoIntegrationActions?.length > 0;

  function renderMenuActions() {
    return (
      <MenuWrapper
        isActive={activeMenu === MENU_ACTIONS}
        closeMenu={closeMenus}
      >
        <MenuButton
          icon={{ name: 'image-multiple', position: 'before' }}
          label={translate('assets.selectedAssetBar.actions')}
          onClick={menuToggler(MENU_ACTIONS)}
        />
        <MenuItemsWrapper>
          <MenuItemSection label={translate('common.photos')} />
          <MenuItemAction
            icon={<i className="mdi mdi-cloud-download-outline" />}
            label={translate('common.download')}
            onClick={actionDownload}
          />
          {renderModalDownload()}
          <MenuItemAction
            icon={<i className="mdi mdi-tag-multiple-outline" />}
            label={translate('assets.selectedAssetBar.tag')}
            onClick={actionTag}
          />
          {renderModalTag()}
          <MenuItemAction
            icon={<i className="mdi mdi-image-multiple-outline" />}
            label={translate('assets.selectedAssetBar.duplicate')}
            onClick={actionDuplicate}
          />
          <MenuItemAction
            icon={<i className="mdi mdi-printer-outline" />}
            label={translate('assets.selectedAssetBar.print')}
            onClick={actionPrint}
          />
          {renderModalPrint()}

          <hr />

          <MenuItemSection
            label={translate('assets.selectedAssetBar.project')}
          />
          <MenuItemAction
            icon={<i className="mdi mdi-image-frame" />}
            label={translate('assets.selectedAssetBar.setCoverPhoto')}
            onClick={actionSetCoverPhoto}
          />
          <MenuItemAction
            icon={<i className="mdi mdi-eye-off-outline" />}
            label={translate('assets.selectedAssetBar.hideInProjectTimeline')}
            onClick={actionMarkInternal}
          />
          <MenuItemAction
            icon={<i className="mdi mdi-eye-outline" />}
            label={translate('assets.selectedAssetBar.unhideInProjectTimeline')}
            onClick={actionUnmarkInternal}
          />
          <MenuItemAction
            icon={<i className="mdi mdi-shuffle" />}
            label={translate('assets.selectedAssetBar.move')}
            onClick={actionMove}
          />
          {renderModalMove()}

          {shouldDisplayIntegrationActions ? (
            <>
              <hr />
              <MenuItemSection
                label={translate('assets.selectedAssetBar.integrations')}
              />

              <IntegrationActionLinks
                integrationActions={photoIntegrationActions}
                isIntegrationActionsLoading={isPhotoIntegrationActionsLoading}
                projectNodeId={projectNodeId}
                projectDatabaseId={projectDatabaseId}
                requestArgs={{ photo_ids: selectedPhotoIds }}
              />
            </>
          ) : null}

          <hr />

          {actionDelete ? (
            <MenuItemAction
              icon={<i className="mdi mdi-delete-outline" />}
              label={
                isTrashCanEnabled
                  ? translate('common.moveToTrash')
                  : translate('common.delete')
              }
              onClick={() => {
                actionDelete();
                Steven.elementClicked({
                  id: 'projects_selected_actions_menu_trashcan',
                  content: 'User clicked trash can from selected actions menu',
                  type: ELEMENT_TYPES.BUTTON,
                  action: 'delete_asset_by_selected_actions_menu_trashcan',
                });
              }}
            />
          ) : null}
        </MenuItemsWrapper>
      </MenuWrapper>
    );
  }

  function renderMenuReports() {
    if (!canAccessReportGeneration) {
      return null;
    }

    const { assetsByType } = menuItemOptions;

    const hasMixedMedia = assetsByType.videos.length > 0;

    const handleAddToReportClick = () => {
      showModal(Modal.ADD_TO_REPORT, {
        assets: menuItemOptions,
      });
    };

    const handleCreateReportClick = () => {
      showModal(Modal.CREATE_REPORT, {
        assets: menuItemOptions,
        projectNodeId,
      });
    };

    const handleCreateReportFromTemplateClick = () => {
      showModal(Modal.CREATE_REPORT_FROM_TEMPLATE, {
        assets: menuItemOptions,
        projectNodeId,
      });
    };

    return (
      <MenuWrapper
        isActive={activeMenu === MENU_REPORTS}
        closeMenu={closeMenus}
      >
        <MenuButton
          icon={{ name: 'file-document', position: 'before' }}
          label={translate('assets.selectedAssetBar.reports')}
          onClick={menuToggler(MENU_REPORTS)}
        />
        <MenuItemsWrapper>
          {hasMixedMedia ? (
            <Notice color="caution" size="small">
              {translate('assets.selectedAssetBar.mixedMediaWarning')}
            </Notice>
          ) : null}

          <MenuItemAction
            icon={<i className="mdi mdi-plus-circle-outline" />}
            label={translate('assets.selectedAssetBar.createNewReport')}
            onClick={handleCreateReportClick}
            showUpgradeIcon={!canAccessReportGeneration}
          />
          <MenuItemAction
            icon={<i className="mdi mdi-text-box-plus-outline" />}
            label={translate('assets.selectedAssetBar.createFromTemplate')}
            onClick={handleCreateReportFromTemplateClick}
            showUpgradeIcon={!canAccessReportTemplates}
            disabled={!canAccessReportTemplates}
          />
          <MenuItemAction
            icon={<i className="mdi mdi-note-plus-outline" />}
            label={translate('assets.selectedAssetBar.addToReport')}
            onClick={handleAddToReportClick}
            showUpgradeIcon={!canAccessReportGeneration}
          />
        </MenuItemsWrapper>
      </MenuWrapper>
    );
  }

  function renderMenuShare() {
    return (
      <MenuWrapper isActive={activeMenu === MENU_SHARE} closeMenu={closeMenus}>
        <MenuButton
          icon={{ name: 'send', position: 'before' }}
          label={translate('common.share')}
          onClick={menuToggler(MENU_SHARE)}
        />
        <MenuItemsWrapper>
          <MenuItemAction
            icon={<i className="mdi mdi-apps" />}
            label={translate('assets.selectedAssetBar.gallery')}
            onClick={actionShareGallery}
            showUpgradeIcon={!hasAccessToShareGallery}
          />
          {renderShareGalleryModal}
        </MenuItemsWrapper>
      </MenuWrapper>
    );
  }

  const renderMenuBeforeAfterPhotos = (
    <MenuItemBeforeAfter
      menuItemOptions={menuItemOptions}
      selectionCount={props.selectionCount}
    />
  );

  return (
    <FloatingActionBar
      isVisible={props.selectionCount > 0}
      onCloseClick={(e) => {
        e?.preventDefault();
        props.onRequestClose();
      }}
      contentLeft={
        <>
          <SelectedContainer>
            <Text as="p" size="m" weight="600" family="heading">
              {translate('common.numberSelected', {
                count: props.selectionCount,
              })}
            </Text>
          </SelectedContainer>
          <FloatingMenuActions data-testid="project__selected-photos-actions">
            {renderMenuActions()}
            {renderMenuReports()}
            {renderMenuShare()}
            {renderMenuBeforeAfterPhotos}
          </FloatingMenuActions>
        </>
      }
    />
  );
}

SelectedAssetBar.propTypes = {
  getAllSelected: PropTypes.func.isRequired,
  selectionCount: PropTypes.number.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  projectNodeId: PropTypes.string,
  projectDatabaseId: PropTypes.string,
};

export default SelectedAssetBar;
